import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Chip,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  EventNote as EventNoteIcon,
  AttachFile as AttachFileIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  LocalHospital as LocalHospitalIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Report as ReportIcon,
  CalendarMonth as CalendarMonthIcon,
  BarChart as BarChartIcon,
  CheckCircle as CheckCircleIcon,
  PauseCircle as PauseCircleIcon,
  RestartAlt as RestartAltIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import historiaService from '../../services/historiaService';
//import pageService from '../../services/pageService';
import documentoService from '../../services/documentoService';
import { useAuth } from '../../hooks/useAuth';
import PageHeader from '../../components/common/PageHeader';
import { formatDate } from '../../utils/dateUtils';
import DashboardProgresoEtapas from '../../components/historias/DashboardProgresoEtapas';
import IndicadoresProgreso from '../../components/historias/IndicadoresProgreso';
import NotificacionCambioEtapa from '../../components/historias/NotificacionCambioEtapa';

// Componentes importados
import SesionList from '../../components/historias/SesionList';
import DocumentosList from '../../components/historias/DocumentosList';
import HistoriaStats from '../../components/historias/HistoriaStats';
import HistoriaTimeline from '../../components/historias/HistoriaTimeline';
import Calendario from '../citas/Calendario';
import ProgresoEtapas from '../../components/historias/ProgresoEtapas';
import CambioEtapa from '../../components/historias/CambioEtapa';
import EstadisticasEtapas from '../../components/historias/EstadisticasEtapas';
import PenalidadesList from '../../components/pacientes/PenalidadesList';
import PlantillasPorEtapa from '../../components/documentos/PlantillasPorEtapa';
import FormulariosPorEtapa from '../../components/historias/FormulariosPorEtapa';
import DocumentosHistoriaView from '../../components/documentos/DocumentosHistoriaView';
import EspecialidadSection from '../../components/historias/EspecialidadSection';


const HistoriaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const isEspecialista = user?.rol === 'Especialista';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historia, setHistoria] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const [sesiones, setSesiones] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [finalizarDialogOpen, setFinalizarDialogOpen] = useState(false);
  const [pausarDialogOpen, setPausarDialogOpen] = useState(false);
  const [reabrirDialogOpen, setReabrirDialogOpen] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Cargar historia clínica
  useEffect(() => {
    const fetchHistoria = async () => {
      setLoading(true);
      setError(null);
      try {
        // Cargar historia clínica
        const response = await historiaService.getHistoria(id);
        
        // Verificar respuesta
        if (!response || !response.data) {
          throw new Error("No se recibieron datos de la historia clínica");
        }
        
        setHistoria(response.data);

        // Cargar sesiones con manejo de errores
        try {
          const sesionesRes = await historiaService.getSesiones(id);
          if (sesionesRes && sesionesRes.data) {
            setSesiones(Array.isArray(sesionesRes.data) ? sesionesRes.data : []);
          } else {
            console.warn("No se recibieron datos de sesiones");
            setSesiones([]);
          }
        } catch (sesionesError) {
          console.error('Error al cargar sesiones:', sesionesError);
          setSesiones([]);
        }

        // Cargar documentos con el método correcto y mejor manejo de errores
        try {
          // CORREGIDO: Usar getDocumentosByHistoria en lugar de getDocumentos
          const docsRes = await documentoService.getDocumentosByHistoria(id);
          
          // Verificar la estructura de la respuesta
          let docsData = [];
          if (docsRes) {
            if (Array.isArray(docsRes)) {
              docsData = docsRes;
            } else if (docsRes.data && Array.isArray(docsRes.data)) {
              docsData = docsRes.data;
            }
          }
          
          setDocumentos(docsData);
        } catch (docsError) {
          console.error('Error al cargar documentos:', docsError);
          setDocumentos([]);
        }
      } catch (error) {
        console.error('Error fetching historia:', error);
        setError(error.message || "Error al cargar la historia clínica");
        showNotification('Error al cargar la historia clínica', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchHistoria();
  }, [id, navigate, showNotification]);

  // Cambiar pestaña
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Obtener color del chip según estado
  const getStatusColor = (estado) => {
    switch (estado) {
      case 'Activo':
        return 'success';
      case 'Finalizado':
        return 'primary';
      case 'En pausa':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Finalizar historia clínica
  const handleFinalizarHistoria = async () => {
    setProcessingAction(true);
    try {
      await historiaService.finalizarHistoria(id, observaciones);
      showNotification('Historia clínica finalizada correctamente', 'success');
      setFinalizarDialogOpen(false);
      
      // Recargar datos
      const response = await historiaService.getHistoria(id);
      if (response && response.data) {
        setHistoria(response.data);
      }
    } catch (error) {
      console.error('Error al finalizar historia clínica:', error);
      showNotification('Error al finalizar la historia clínica', 'error');
    } finally {
      setProcessingAction(false);
      setObservaciones('');
    }
  };

  // Pausar historia clínica
  const handlePausarHistoria = async () => {
    setProcessingAction(true);
    try {
      await historiaService.updateHistoria(id, { 
        estado: 'En pausa',
        notas_finalizacion: observaciones 
      });
      showNotification('Historia clínica pausada correctamente', 'success');
      setPausarDialogOpen(false);
      
      // Recargar datos
      const response = await historiaService.getHistoria(id);
      if (response && response.data) {
        setHistoria(response.data);
      }
    } catch (error) {
      console.error('Error al pausar historia clínica:', error);
      showNotification('Error al pausar la historia clínica', 'error');
    } finally {
      setProcessingAction(false);
      setObservaciones('');
    }
  };

  // Reabrir historia clínica
  const handleReabrirHistoria = async () => {
    setProcessingAction(true);
    try {
      await historiaService.reabrirHistoria(id, observaciones);
      showNotification('Historia clínica reabierta correctamente', 'success');
      setReabrirDialogOpen(false);
      
      // Recargar datos
      const response = await historiaService.getHistoria(id);
      if (response && response.data) {
        setHistoria(response.data);
      }
    } catch (error) {
      console.error('Error al reabrir historia clínica:', error);
      showNotification('Error al reabrir la historia clínica', 'error');
    } finally {
      setProcessingAction(false);
      setObservaciones('');
    }
  };

  // Imprimir historia clínica
  const handlePrintHistoria = () => {
    navigate(`/historias/${id}/imprimir`);
  };

  // Recargar datos
  const handleRefreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Cargar historia
      const response = await historiaService.getHistoria(id);
      
      if (!response || !response.data) {
        throw new Error("No se recibieron datos al recargar la historia clínica");
      }
      
      setHistoria(response.data);

      // Recargar sesiones con manejo de errores
      try {
        const sesionesRes = await historiaService.getSesiones(id);
        if (sesionesRes && sesionesRes.data) {
          setSesiones(Array.isArray(sesionesRes.data) ? sesionesRes.data : []);
        }
      } catch (error) {
        console.error('Error al recargar sesiones:', error);
      }

      // Recargar documentos con método correcto
      try {
        // CORREGIDO: Usar getDocumentosByHistoria en lugar de getDocumentos
        const docsRes = await documentoService.getDocumentosByHistoria(id);
        
        // Verificar la estructura de la respuesta
        let docsData = [];
        if (docsRes) {
          if (Array.isArray(docsRes)) {
            docsData = docsRes;
          } else if (docsRes.data && Array.isArray(docsRes.data)) {
            docsData = docsRes.data;
          }
        }
        
        setDocumentos(docsData);
      } catch (error) {
        console.error('Error al recargar documentos:', error);
      }
    } catch (error) {
      console.error('Error al recargar datos:', error);
      setError(error.message || "Error al recargar datos");
      showNotification('Error al recargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/historias')}
        >
          Volver a Historias Clínicas
        </Button>
      </Box>
    );
  }

  if (!historia) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="error" gutterBottom>
          Error al cargar la historia clínica
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/historias')}
        >
          Volver a Historias Clínicas
        </Button>
      </Box>
    );
  }

  // SEGURIDAD: Acceso seguro a propiedades anidadas
  const paciente = historia.paciente || {};
  const pacienteNombre = paciente.nombres || 'Sin nombre';
  const pacienteApellidos = paciente.apellidos || 'Sin apellidos';
  const pacienteNumeroHistoria = paciente.numero_historia || 'N/A';
  const pacienteFoto = paciente.foto || '/static/images/avatar/default.jpg';
  const pacienteEdad = paciente.edad || 'N/A';
  const pacienteSexo = paciente.sexo || '';
  const sexoTexto = pacienteSexo === 'M' ? 'Masculino' : pacienteSexo === 'F' ? 'Femenino' : 'Otro';
  const pacienteDni = paciente.dni || 'N/A';
  const pacienteFechaNacimiento = paciente.fecha_nacimiento;
  const pacienteTelefono = paciente.telefono || 'No registrado';
  const pacienteEmail = paciente.email || 'No registrado';

  // Acceso seguro a datos del especialista y especialidad
  const especialista = historia.especialista || {};
  const especialistaUsuario = especialista.usuario || {};
  const especialistaNombre = especialistaUsuario.nombre || '';
  const especialistaApellidos = especialistaUsuario.apellidos || '';
  
  const especialidad = historia.especialidad || {};
  const especialidadNombre = especialidad.nombre || 'N/A';

  // Acciones según el estado de la historia
  const renderStatusActions = () => {
    if (historia.estado === 'Activo') {
      return (
        <>
          <Button 
            variant="outlined" 
            color="primary"
            startIcon={<CheckCircleIcon />}
            onClick={() => setFinalizarDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Finalizar
          </Button>
          <Button 
            variant="outlined" 
            color="warning"
            startIcon={<PauseCircleIcon />}
            onClick={() => setPausarDialogOpen(true)}
          >
            Pausar
          </Button>
        </>
      );
    } else {
      return (
        <Button 
          variant="outlined" 
          color="success"
          startIcon={<RestartAltIcon />}
          onClick={() => setReabrirDialogOpen(true)}
        >
          Reabrir
        </Button>
      );
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title={`Historia Clínica: ${pacienteNombre} ${pacienteApellidos}`}
        subtitle={`N° Historia: ${pacienteNumeroHistoria}`}
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Historias Clínicas', to: '/historias' },
          { text: 'Detalle Historia' }
        ]}
        actionButton={
          <Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/historias')}
              sx={{ mr: 1 }}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              component={RouterLink}
              to={`/historias/editar/${historia.id}`}
              sx={{ mr: 1 }}
            >
              Editar
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={handlePrintHistoria}
            >
              Imprimir
            </Button>
          </Box>
        }
      />

      <Grid container spacing={3}>
        {/* Información general */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" gutterBottom>
                  Información del Paciente
                </Typography>
                {historia.paciente_id && (
                  <Button
                    size="small"
                    component={RouterLink}
                    to={`/pacientes/${historia.paciente_id}`}
                    startIcon={<VisibilityIcon />}
                  >
                    Ver Perfil
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar
                  src={pacienteFoto}
                  sx={{ width: 56, height: 56, mr: 2 }}
                />
                <Box>
                  <Typography variant="subtitle1">
                    {pacienteNombre} {pacienteApellidos}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {pacienteEdad} años • {sexoTexto}
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">DNI</Typography>
                  <Typography variant="body1">{pacienteDni}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Fecha de Nacimiento</Typography>
                  <Typography variant="body1">{pacienteFechaNacimiento ? formatDate(pacienteFechaNacimiento) : 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Teléfono</Typography>
                  <Typography variant="body1">{pacienteTelefono}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1" noWrap>{pacienteEmail}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" gutterBottom>
                  Información de la Historia
                </Typography>
                <Chip 
                  label={historia.estado || 'Desconocido'} 
                  color={getStatusColor(historia.estado)}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Especialista</Typography>
                  <Typography variant="body1" noWrap>
                    {especialistaNombre} {especialistaApellidos}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Especialidad</Typography>
                  <Typography variant="body1">{especialidadNombre}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Fecha de Creación</Typography>
                  <Typography variant="body1">{historia.fecha_creacion ? formatDate(historia.fecha_creacion) : 'N/A'}</Typography>
                </Grid>
                {historia.fecha_finalizacion && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Fecha de Finalización</Typography>
                    <Typography variant="body1">{formatDate(historia.fecha_finalizacion)}</Typography>
                  </Grid>
                )}
                {historia.diagnostico_cie10 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Diagnóstico CIE-10</Typography>
                    <Typography variant="body1">{historia.diagnostico_cie10}</Typography>
                  </Grid>
                )}
                {/* Sección de estadísticas por etapas - Solo si hay IDs necesarios */}
                {historia.id && historia.programa_id && (
                  <Grid item xs={12} md={6}>
                    <EstadisticasEtapas 
                      historiaId={historia.id}
                      programaId={historia.programa_id}
                    />
                  </Grid>
                )}

                {/* Sección de penalidades - Solo si hay paciente_id */}
                {historia.paciente_id && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                      <PenalidadesList pacienteId={historia.paciente_id} />
                    </Paper>
                  </Grid>
                )}
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" justifyContent="center">
                {renderStatusActions()}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pestañas y contenido principal */}
        <Grid item xs={12} md={8}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab icon={<DescriptionIcon />} iconPosition="start" label="INFORMACIÓN GENERAL" />
                {historia.especialidades && historia.especialidades.map((esp, index) => (
                  <Tab 
                    key={esp.id} 
                    icon={<LocalHospitalIcon />} 
                    iconPosition="start" 
                    label={esp.especialidad.nombre.toUpperCase()} 
                  />
                ))}
                <Tab icon={<AttachFileIcon />} iconPosition="start" label="DOCUMENTOS" />
                <Tab icon={<TimelineIcon />} iconPosition="start" label="LÍNEA DE TIEMPO" />
              </Tabs>
            </Box>
            <CardContent>
              {/* Tab de información general */}
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Información General del Paciente
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Motivo de Consulta General
                        </Typography>
                        <Typography variant="body1">
                          {historia.motivo_consulta_general || 'No registrado'}
                        </Typography>
                      </Grid>
                      {/* Más información general... */}
                    </Grid>
                  </Paper>
                </Box>
              )}

              {/* Tabs de especialidades */}
              {historia.especialidades && historia.especialidades.map((esp, index) => (
                tabValue === index + 1 && (
                  <Box key={esp.id}>
                    <EspecialidadSection 
                      seccion={esp}
                      historiaId={historia.id}
                      onUpdate={handleRefreshData}
                    />
                  </Box>
                )
              ))}

              {/* Tab de documentos */}
              {tabValue === (historia.especialidades?.length || 0) + 1 && (
                <DocumentosHistoriaView
                  historiaClinicaId={historia.id}
                  pacienteId={historia.paciente_id}
                  onDocumentoCreado={handleRefreshData}
                />
              )}

              {/* Tab de línea de tiempo */}
              {tabValue === (historia.especialidades?.length || 0) + 2 && (
                <HistoriaTimeline historiaId={historia.id} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Diálogo para finalizar historia */}
      <Dialog 
        open={finalizarDialogOpen} 
        onClose={() => setFinalizarDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Finalizar Historia Clínica</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Al finalizar esta historia clínica, se marcará como completada y no se podrán realizar más cambios a menos que la reabra.
          </Typography>
          <TextField
            fullWidth
            label="Observaciones de finalización"
            multiline
            rows={4}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizarDialogOpen(false)} disabled={processingAction}>
            Cancelar
          </Button>
          <Button 
            onClick={handleFinalizarHistoria} 
            color="primary" 
            variant="contained"
            disabled={processingAction}
            startIcon={processingAction && <CircularProgress size={20} />}
          >
            Finalizar Historia
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para pausar historia */}
      <Dialog 
        open={pausarDialogOpen} 
        onClose={() => setPausarDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pausar Historia Clínica</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Al pausar esta historia clínica, se suspenderá temporalmente el tratamiento. Podrá reanudarla en cualquier momento.
          </Typography>
          <TextField
            fullWidth
            label="Motivo de la pausa"
            multiline
            rows={4}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPausarDialogOpen(false)} disabled={processingAction}>
            Cancelar
          </Button>
          <Button 
            onClick={handlePausarHistoria} 
            color="warning" 
            variant="contained"
            disabled={processingAction}
            startIcon={processingAction && <CircularProgress size={20} />}
          >
            Pausar Historia
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para reabrir historia */}
      <Dialog 
        open={reabrirDialogOpen} 
        onClose={() => setReabrirDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reabrir Historia Clínica</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Al reabrir esta historia clínica, se reanudará el tratamiento y podrá realizar cambios nuevamente.
          </Typography>
          <TextField
            fullWidth
            label="Motivo de reapertura"
            multiline
            rows={4}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReabrirDialogOpen(false)} disabled={processingAction}>
            Cancelar
          </Button>
          <Button 
            onClick={handleReabrirHistoria} 
            color="success" 
            variant="contained"
            disabled={processingAction}
            startIcon={processingAction && <CircularProgress size={20} />}
          >
            Reabrir Historia
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoriaDetail;